import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { useUserContext } from "../../app/protectedRoute/UserContext";
import * as paymentService from "../../api/services/paymentService";
import * as userService from "../../api/services/userService";
import * as guestService from "../../api/services/guestService";
import * as hostService from "../../api/services/hostService";
import { Booking, CategorizedBookings } from "../../types/bookingTypes";
import CreateAvailability from "../../app/protected/Host/HomePage/MyProperty/CreateRoomAvailability/CreateRoomAvailability";
import CreateBedAvailability from "../../app/protected/Host/HomePage/MyProperty/CreateBedAvailability/CreateBedAvailability";
import UpdateRoomAvailability from "../../app/protected/Host/HomePage/MyProperty/UpdateRoomAvailability/UpdateRoomAvailability";
import UpdateBedAvailability from "../../app/protected/Host/HomePage/MyProperty/UpdateBedAvailability/UpdateBedAvailability";
import { BedDouble, CalendarPlus, Edit, User, CalendarCheck, CalendarX, Calendar, DoorOpen, Plus, Users, IndianRupee, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from "../../lib/utils";
import { IViewBedDurationReponse, IViewRoomDurationReponse } from "../../app/model/hostModel";
import {
  Dialog as ShadcnDialog,
  DialogContent as ShadcnDialogContent,
  DialogHeader as ShadcnDialogHeader,
  DialogTitle as ShadcnDialogTitle,
  DialogDescription as ShadcnDialogDescription,
} from '../ui/dialog';

interface ShortTermVacancyProps {
    propertyId: string;
}

const ShortTermVacancy: React.FC<ShortTermVacancyProps> = ({ propertyId }) => {
    const [activeTab, setActiveTab] = useState('booking');
    const [propertyViewTab, setPropertyViewTab] = useState<"beds" | "rooms">("beds");

    // State and functions from HostBooking
    const { state } = useUserContext();
    const [loading, setLoading] = useState<boolean>(true);
    const [categorizedBookings, setCategorizedBookings] = useState<CategorizedBookings>({
        Upcoming: [],
        Ongoing: [],
        Completed: [],
        Cancelled: [],
    });
    const [selectedTab, setSelectedTab] = useState<string>("Upcoming");
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    // State and functions from MyProperty
    const [beds, setBeds] = useState<IViewBedDurationReponse[]>([]);
    const [rooms, setRooms] = useState<IViewRoomDurationReponse[]>([]);
    const [showAddBedDialog, setShowAddBedDialog] = useState(false);
    const [showEditBedDialog, setShowEditBedDialog] = useState(false);
    const [selectedBed, setSelectedBed] = useState<any | null>(null);
    const [showAddRoomDialog, setShowAddRoomDialog] = useState(false);
    const [showEditRoomDialog, setShowEditRoomDialog] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState<any | null>(null);


    useEffect(() => {
        if (propertyId) {
            fetchCategorizedBookings(propertyId);
            fetchBeds(propertyId);
            fetchRooms(propertyId);
        }
    }, [propertyId]);

    const fetchCategorizedBookings = async (id: string) => {
        setLoading(true);
        try {
            const response = await paymentService.getOrdersByProperty(id);
            setCategorizedBookings(response);
        } catch (error) {
            console.error("Failed to fetch categorized bookings:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBeds = async (id: string) => {
        try {
            const response = await hostService.getViewBedDuration(id);
            setBeds(response);
        } catch (error) {
            console.error("Failed to fetch beds:", error);
        }
    };

    const fetchRooms = async (id: string) => {
        try {
            const response = await hostService.getViewRoomDuration(id);
            setRooms(response);
        } catch (error) {
            console.error("Failed to fetch rooms:", error);
        }
    };

    const handleToggleBedAutoList = async (bed: IViewBedDurationReponse) => {
        if (!bed.bed) return;
        const newValue = !bed.auto_list;
        setBeds(prev => prev.map(b => b.id === bed.id ? { ...b, auto_list: newValue } : b));
        try {
            await hostService.patchBed(bed.bed, { auto_list: newValue });
        } catch (error) {
            setBeds(prev => prev.map(b => b.id === bed.id ? { ...b, auto_list: !newValue } : b));
            console.error("Failed to toggle bed auto-list:", error);
        }
    };

    const handleToggleRoomAutoList = async (room: IViewRoomDurationReponse) => {
        if (!room.room) return;
        const newValue = !room.auto_list;
        setRooms(prev => prev.map(r => r.id === room.id ? { ...r, auto_list: newValue } : r));
        try {
            await hostService.patchRoom(room.room, { auto_list: newValue });
        } catch (error) {
            setRooms(prev => prev.map(r => r.id === room.id ? { ...r, auto_list: !newValue } : r));
            console.error("Failed to toggle room auto-list:", error);
        }
    };

    const handleCardClick = (booking: Booking) => {
        setSelectedBooking(booking);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedBooking(null);
    };

    const handleCheckInClick = async (bookingId: string) => {
        try {
            setLoading(true);
            await userService.checkInGuest(bookingId, '');
            if (propertyId) fetchCategorizedBookings(propertyId);
        } catch (error: any) {
            console.error("Check-in failed:", error.response?.data?.error || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckoutClick = async (bookingId: string) => {
        try {
            setLoading(true);
            await guestService.checkoutBooking(bookingId);
            if (propertyId) {
                fetchCategorizedBookings(propertyId);
            }
        } catch (error) {
            console.error("Checkout failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadInvoice = async (bookingId: string) => {
        try {
            const response = await paymentService.getInvoiceByBooking(bookingId);
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${bookingId}.html`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } catch (error) {
            console.error("Failed to download invoice:", error);
        }
    };

    const BookingCard = ({ booking, showCheckIn = false, showCheckOut = false }: { booking: Booking, showCheckIn?: boolean, showCheckOut?: boolean }) => (
        <div className="bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:border-primary/30" onClick={() => handleCardClick(booking)}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-lg">{booking.guest_name}</h3>
                <p className="text-sm text-muted-foreground">{booking.item_details.name}</p>
                <div className="flex flex-wrap gap-4 mt-2 text-sm">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <CalendarCheck className="h-4 w-4 text-primary" />
                    <span>Check-in: <span className="text-foreground font-medium">{booking.checkin}</span></span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <CalendarX className="h-4 w-4 text-destructive" />
                    <span>Check-out: <span className="text-foreground font-medium">{booking.checkout}</span></span>
                  </div>
                </div>
                <p className="text-primary font-bold text-lg mt-2">₹{booking.total_price}.00</p>
                {showCheckIn && (
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-sm">
                    <IndianRupee className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-amber-800 font-medium">Collect ₹{booking.total_price} from guest on arrival</span>
                  </div>
                )}
              </div>
            </div>
            {showCheckIn && (
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => handleCheckInClick(booking.booking_id)}>
                CHECK-IN
              </Button>
            )}
             {showCheckOut && (
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => handleCheckoutClick(booking.booking_id)}>
                    Check-out
                </Button>
            )}
          </div>
        </div>
      );

    const renderBookingContent = () => {
        if (loading) {
            return (
                <Box className="flex justify-center items-center h-full">
                    <CircularProgress />
                </Box>
            );
        }

        const currentBookings = categorizedBookings[selectedTab as keyof CategorizedBookings] || [];

        return (
            <div>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                    {(["Upcoming", "Ongoing", "Completed", "Cancelled"] as const).map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setSelectedTab(filter)}
                        className={cn(
                          "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                          selectedTab === filter
                            ? "bg-primary/10 text-primary border-b-2 border-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                      >
                        {filter.toUpperCase()}
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {categorizedBookings[filter as keyof CategorizedBookings].length}
                        </Badge>
                      </button>
                    ))}
                  </div>

                <div className="space-y-4 mt-4">
                    {currentBookings.length === 0 ? (
                        <div className="text-center py-12">
                        <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No {selectedTab.toLowerCase()} bookings</p>
                      </div>
                    ) : (
                        currentBookings.map((booking: Booking, index: number) => (
                            <BookingCard 
                                key={index} 
                                booking={booking} 
                                showCheckIn={selectedTab === "Upcoming"}
                                showCheckOut={selectedTab === "Ongoing"}
                            />
                        ))
                    )}
                </div>

                <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                    <DialogTitle>Booking Details</DialogTitle>
                    <DialogContent>
                        {selectedBooking && (
                            <Box>
                                <Typography variant="body1">
                                    <strong>Guest Name:</strong> {selectedBooking.guest_name}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Guest Phone:</strong> {selectedBooking.guest_phone_number}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Check-in Date:</strong> {selectedBooking.checkin}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Check-out Date:</strong> {selectedBooking.checkout}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Total Price:</strong> ₹{selectedBooking.total_price}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Status:</strong> {selectedBooking.status}
                                </Typography>
                                {selectedTab === 'Completed' && (
                                    <>
                                        <Typography variant="body1">
                                            <strong>Payout Status:</strong> {selectedBooking.payout_status}
                                        </Typography>
                                        <Button 
                                            variant="default" 
                                            onClick={() => handleDownloadInvoice(selectedBooking.booking_id)}
                                        >
                                            Download Invoice
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outline" onClick={handleCloseDialog}>Close</Button>
                    </DialogActions>
                </Dialog>

            </div>
        );
    };

    const BedCard = ({ bed }: { bed: IViewBedDurationReponse }) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const isListed = !!(bed.start_date && bed.end_date);
        const isExpired = isListed && new Date(bed.end_date!) < today;
        return (
          <div className={cn(
            "bg-card border rounded-xl p-5 transition-all duration-300 hover:shadow-lg relative overflow-hidden",
            !isListed ? "border-orange-300 bg-orange-50/50"
              : isExpired ? "border-yellow-300 bg-yellow-50/50"
              : "border-green-300 bg-green-50/50"
          )}>
            <Badge
              className={cn(
                "absolute top-3 right-3",
                !isListed ? "bg-orange-500/10 text-orange-700 border-orange-400"
                  : isExpired ? "bg-yellow-500/10 text-yellow-700 border-yellow-400"
                  : "bg-green-500/10 text-green-700 border-green-400"
              )}
              variant="outline"
            >
              {!isListed ? "Not Listed" : isExpired ? "⚠ Expired" : "✓ Listed"}
            </Badge>

            <div className="flex items-start gap-3 mb-4">
              <div className={cn(
                "p-2.5 rounded-lg",
                !isListed ? "bg-orange-100" : isExpired ? "bg-yellow-100" : "bg-green-100"
              )}>
                <BedDouble className={cn("h-5 w-5",
                  !isListed ? "text-orange-600" : isExpired ? "text-yellow-600" : "text-green-600"
                )} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{bed.bed_category}</h3>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> Capacity
                </span>
                <span className="font-medium">{bed.capacity}</span>
              </div>
            </div>

            <div className={cn(
              "rounded-lg p-3 mb-3",
              !isListed ? "bg-orange-100 border border-orange-300"
                : isExpired ? "bg-yellow-100 border border-yellow-300"
                : "bg-green-100 border border-green-300"
            )}>
              <p className="text-xs font-medium mb-1 text-muted-foreground">📅 Availability Period</p>
              {isListed ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className={cn("font-medium", isExpired ? "text-yellow-800" : "text-green-800")}>
                    {new Date(bed.start_date!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} → {new Date(bed.end_date!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-orange-800 font-medium">⚠️ Set dates to start earning</p>
              )}
            </div>

            {/* Auto-list toggle */}
            <div
              className={cn(
                "flex items-center justify-between rounded-lg px-3 py-2 mb-3 border select-none transition-colors",
                !isListed
                  ? "opacity-40 cursor-not-allowed bg-gray-50 border-gray-200"
                  : bed.auto_list
                    ? "cursor-pointer bg-green-50 border-green-300"
                    : "cursor-pointer bg-gray-50 border-gray-200"
              )}
              onClick={() => isListed && handleToggleBedAutoList(bed)}
              title={!isListed ? "List this bed first to enable auto-list" : ""}
            >
              <div className="flex items-center gap-2">
                <RefreshCw className={cn("h-3.5 w-3.5", bed.auto_list && isListed ? "text-green-600" : "text-gray-400")} />
                <span className={cn("text-xs font-medium", bed.auto_list && isListed ? "text-green-700" : "text-gray-500")}>
                  Auto-list
                </span>
                <span className="text-xs text-muted-foreground">
                  {!isListed ? "(list first to enable)" : "(extends 1 month on expiry)"}
                </span>
              </div>
              <div className={cn(
                "w-9 h-5 rounded-full transition-colors flex items-center px-0.5",
                bed.auto_list && isListed ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
              )}>
                <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
              </div>
            </div>

            <Button
              variant={isListed ? "outline" : "default"}
              className={cn("w-full", !isListed && "bg-orange-500 hover:bg-orange-600")}
              onClick={() => { setSelectedBed(bed); setShowEditBedDialog(true); }}
            >
              <Edit className="h-4 w-4 mr-2" />
              {!isListed ? "Set Dates & List Now" : isExpired ? "Update Dates" : "Edit Availability"}
            </Button>
          </div>
        );
      };
    
      const RoomCard = ({ room }: { room: IViewRoomDurationReponse }) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const isListed = !!(room.start_date && room.end_date);
        const isExpired = isListed && new Date(room.end_date!) < today;
        return (
          <div className={cn(
            "bg-card border rounded-xl p-5 transition-all duration-300 hover:shadow-lg relative overflow-hidden",
            !isListed ? "border-orange-300 bg-orange-50/50"
              : isExpired ? "border-yellow-300 bg-yellow-50/50"
              : "border-green-300 bg-green-50/50"
          )}>
            <Badge
              className={cn(
                "absolute top-3 right-3",
                !isListed ? "bg-orange-500/10 text-orange-700 border-orange-400"
                  : isExpired ? "bg-yellow-500/10 text-yellow-700 border-yellow-400"
                  : "bg-green-500/10 text-green-700 border-green-400"
              )}
              variant="outline"
            >
              {!isListed ? "Not Listed" : isExpired ? "⚠ Expired" : "✓ Listed"}
            </Badge>

            <div className="flex items-start gap-3 mb-4">
              <div className={cn(
                "p-2.5 rounded-lg",
                !isListed ? "bg-orange-100" : isExpired ? "bg-yellow-100" : "bg-green-100"
              )}>
                <DoorOpen className={cn("h-5 w-5",
                  !isListed ? "text-orange-600" : isExpired ? "text-yellow-600" : "text-green-600"
                )} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Room {room.room_number}</h3>
              </div>
            </div>

            <div className={cn(
              "rounded-lg p-3 mb-3",
              !isListed ? "bg-orange-100 border border-orange-300"
                : isExpired ? "bg-yellow-100 border border-yellow-300"
                : "bg-green-100 border border-green-300"
            )}>
              <p className="text-xs font-medium mb-1 text-muted-foreground">📅 Availability Period</p>
              {isListed ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className={cn("font-medium", isExpired ? "text-yellow-800" : "text-green-800")}>
                    {new Date(room.start_date!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} → {new Date(room.end_date!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-orange-800 font-medium">⚠️ Set dates to start earning</p>
              )}
            </div>

            {/* Auto-list toggle */}
            <div
              className={cn(
                "flex items-center justify-between rounded-lg px-3 py-2 mb-3 border select-none transition-colors",
                !isListed
                  ? "opacity-40 cursor-not-allowed bg-gray-50 border-gray-200"
                  : room.auto_list
                    ? "cursor-pointer bg-green-50 border-green-300"
                    : "cursor-pointer bg-gray-50 border-gray-200"
              )}
              onClick={() => isListed && handleToggleRoomAutoList(room)}
              title={!isListed ? "List this room first to enable auto-list" : ""}
            >
              <div className="flex items-center gap-2">
                <RefreshCw className={cn("h-3.5 w-3.5", room.auto_list && isListed ? "text-green-600" : "text-gray-400")} />
                <span className={cn("text-xs font-medium", room.auto_list && isListed ? "text-green-700" : "text-gray-500")}>
                  Auto-list
                </span>
                <span className="text-xs text-muted-foreground">
                  {!isListed ? "(list first to enable)" : "(extends 1 month on expiry)"}
                </span>
              </div>
              <div className={cn(
                "w-9 h-5 rounded-full transition-colors flex items-center px-0.5",
                room.auto_list && isListed ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
              )}>
                <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
              </div>
            </div>

            <Button
              variant={isListed ? "outline" : "default"}
              className={cn("w-full", !isListed && "bg-orange-500 hover:bg-orange-600")}
              onClick={() => { setSelectedRoom(room); setShowEditRoomDialog(true); }}
            >
              <Edit className="h-4 w-4 mr-2" />
              {!isListed ? "Set Dates & List Now" : isExpired ? "Update Dates" : "Edit Availability"}
            </Button>
          </div>
        );
      };

    const renderMyPropertyContent = () => {
        return (
            <div>
            {/* Beds/Rooms Toggle */}
            <div className="flex gap-2 mb-6">
              <Button
                variant={propertyViewTab === "beds" ? "default" : "outline"}
                onClick={() => setPropertyViewTab("beds")}
                className="flex-1 gap-2"
              >
                <BedDouble className="h-4 w-4" />
                Beds
                <Badge variant="secondary" className="ml-1">{beds.length}</Badge>
              </Button>
              <Button
                variant={propertyViewTab === "rooms" ? "default" : "outline"}
                onClick={() => setPropertyViewTab("rooms")}
                className="flex-1 gap-2"
              >
                <DoorOpen className="h-4 w-4" />
                Rooms
                <Badge variant="secondary" className="ml-1">{rooms.length}</Badge>
              </Button>
            </div>

            {/* Beds View */}
            {propertyViewTab === "beds" && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-1">Manage Your Beds</h2>
                    <p className="text-sm text-muted-foreground">Add beds and set availability to start earning</p>
                  </div>
                  <Button className="gap-2" onClick={() => setShowAddBedDialog(true)}>
                    <Plus className="h-4 w-4" />
                    Add New Bed
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {beds.map((bed) => (
                      <BedCard key={bed.id} bed={bed} />
                  ))}
                </div>
              </div>
            )}

            {/* Rooms View */}
            {propertyViewTab === "rooms" && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-1">Manage Your Rooms</h2>
                    <p className="text-sm text-muted-foreground">Add rooms and set availability to start earning</p>
                  </div>
                  <Button className="gap-2" onClick={() => setShowAddRoomDialog(true)}>
                    <Plus className="h-4 w-4" />
                    Add New Room
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rooms.map((room) => (
                      <RoomCard key={room.id} room={room} />
                  ))}
                </div>
              </div>
            )}

            <CreateBedAvailability open={showAddBedDialog} onClose={() => setShowAddBedDialog(false)} onBedCreated={() => fetchBeds(propertyId)} />
            <ShadcnDialog open={showEditBedDialog} onOpenChange={(open) => { if (!open) { setShowEditBedDialog(false); setSelectedBed(null); } }}>
                <ShadcnDialogContent className="sm:max-w-[450px] bg-card">
                    <ShadcnDialogHeader>
                        <ShadcnDialogTitle className="flex items-center gap-2 text-xl">
                            <Calendar className="h-5 w-5 text-primary" />
                            Set Availability Dates
                        </ShadcnDialogTitle>
                        <ShadcnDialogDescription>
                            Update when this bed is available for booking
                        </ShadcnDialogDescription>
                    </ShadcnDialogHeader>
                    <UpdateBedAvailability bed={selectedBed} onBedAvailabilityUpdated={() => {
                        fetchBeds(propertyId);
                        setShowEditBedDialog(false);
                        setSelectedBed(null);
                    }} onCancel={() => { setShowEditBedDialog(false); setSelectedBed(null); }} />
                </ShadcnDialogContent>
            </ShadcnDialog>
            <CreateAvailability open={showAddRoomDialog} onClose={() => setShowAddRoomDialog(false)} onRoomCreated={() => fetchRooms(propertyId)} />
            <ShadcnDialog open={showEditRoomDialog} onOpenChange={(open) => { if (!open) { setShowEditRoomDialog(false); setSelectedRoom(null); } }}>
                <ShadcnDialogContent className="sm:max-w-[450px] bg-card">
                    <ShadcnDialogHeader>
                        <ShadcnDialogTitle className="flex items-center gap-2 text-xl">
                            <Calendar className="h-5 w-5 text-primary" />
                            Set Availability Dates
                        </ShadcnDialogTitle>
                        <ShadcnDialogDescription>
                            Update when this room is available for booking
                        </ShadcnDialogDescription>
                    </ShadcnDialogHeader>
                    <UpdateRoomAvailability room={selectedRoom} onRoomAvailabilityUpdated={() => {
                        fetchRooms(propertyId);
                        setShowEditRoomDialog(false);
                        setSelectedRoom(null);
                    }} onCancel={() => { setShowEditRoomDialog(false); setSelectedRoom(null); }} />
                </ShadcnDialogContent>
            </ShadcnDialog>

          </div>
        )
    };

    return (
        <div>
            <div className="border-b border-border mb-6">
                <div className="flex gap-6">
                  <button
                    onClick={() => setActiveTab("booking")}
                    className={cn(
                      "pb-3 px-1 font-medium transition-colors relative",
                      activeTab === "booking"
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    BOOKING
                    {activeTab === "booking" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("my-property")}
                    className={cn(
                      "pb-3 px-1 font-medium transition-colors relative",
                      activeTab === "my-property"
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    MY PROPERTY
                    {activeTab === "my-property" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </button>
                </div>
              </div>
            <div>
                {activeTab === 'booking' && renderBookingContent()}
                {activeTab === 'my-property' && renderMyPropertyContent()}
            </div>
        </div>
    );
};

export default ShortTermVacancy;